/**
 * ACC-B · La cuenta, vista desde el navegador.
 *
 * Habla con el servicio de identidad (`VITE_IDENTITY_URL`) y guarda en memoria
 * lo que hace falta para pintar el perfil y para conectarse a una partida.
 *
 * Dos cosas que NO hace, y las dos a propósito:
 *
 *  - **No guarda la sesión.** La sesión vive en una cookie `HttpOnly` que este
 *    código no puede leer. Por eso todas las llamadas van con
 *    `credentials: "include"` y por eso saber si hay sesión es preguntar
 *    (`GET /account/me`), no mirar `localStorage`. Es más lento y es lo correcto:
 *    una sesión que el JavaScript puede leer es una sesión que se lleva un XSS.
 *
 *  - **No obliga a nada.** Sin `VITE_IDENTITY_URL` el módulo entero se apaga
 *    (`enabled === false`) y el juego se comporta exactamente como antes: se
 *    juega de invitado y no aparece ni un botón que no lleve a ningún sitio.
 *    Es la misma regla que en el edge con `SESSION_SECRET`.
 *
 * El pase para el servidor de partida se cachea aquí porque la URL del
 * WebSocket se construye de forma síncrona en cada intento de conexión. Dura dos
 * minutos; se refresca antes de jugar y cuando queda poco.
 */

/** Lo que el servicio cuenta de tu cuenta. Nunca incluye correos. */
export interface AccountView {
  id: string;
  nickname: string;
  displayName: string;
  coins: number;
  entitlements: string[];
  /** Lo que llevas puesto, por hueco (`{ icon: "bolt" }`). Ya limpio por el servidor. */
  loadout: Record<string, string>;
  providers: string[];
  createdAt: number;
}

/** Resultado de volver del proveedor, leído de la URL. */
export type AuthOutcome =
  | { kind: "none" }
  | { kind: "in" }                              // sesión abierta
  | { kind: "linked" }                          // proveedor vinculado
  | { kind: "nuevo"; suggestion: string }       // falta elegir nick
  | { kind: "error"; reason: string };

/** URL del servicio. Vacía = cuentas apagadas en este despliegue. */
const BASE = (import.meta.env?.VITE_IDENTITY_URL as string | undefined)?.replace(/\/$/, "") ?? "";

/** ¿Hay cuentas en este despliegue? */
export const enabled = (): boolean => BASE !== "";

/* ---------------- inyección para pruebas ---------------- */

type Fetcher = typeof fetch;
let http: Fetcher = ((...a: Parameters<typeof fetch>) => fetch(...a)) as Fetcher;
let baseOverride: string | null = null;

/** Las pruebas sustituyen el transporte y la URL; en producción no se llama. */
export function setAccountTransport(f: Fetcher | null, base?: string): void {
  http = f ?? (((...a: Parameters<typeof fetch>) => fetch(...a)) as Fetcher);
  baseOverride = base ?? null;
}

const url = (p: string): string => `${baseOverride ?? BASE}${p}`;
const on = (): boolean => (baseOverride ?? BASE) !== "";

async function call<T>(p: string, init: RequestInit = {}): Promise<T | null> {
  if (!on()) return null;
  try {
    const res = await http(url(p), {
      ...init,
      // Sin esto el navegador no manda la cookie de sesión a otro origen, y
      // todas las respuestas dirían "no hay cuenta" aunque la haya.
      credentials: "include",
      headers: { "content-type": "application/json", ...(init.headers ?? {}) },
    });
    // `null` significa UNA cosa: no hubo respuesta que interpretar. Un 4xx sí es
    // una respuesta —el servicio contestó, y contestó que no— y trae el motivo
    // en el cuerpo. Colapsar los dos casos hacía que "todavía no es tuyo"
    // apareciera en pantalla como "no se pudo contactar con el servicio", que
    // manda a mirar la red cuando el problema es otro.
    if (!res.ok && (res.status < 400 || res.status >= 500)) return null;
    return (await res.json()) as T;
  } catch {
    // Que el servicio de identidad no conteste NO puede romper el juego: se
    // sigue como invitado. Ese es todo el motivo de que los edges no dependan
    // de él.
    return null;
  }
}

/* ---------------- estado en memoria ---------------- */

let cuenta: AccountView | null = null;
let proveedores: string[] = [];
/**
 * ¿Contestó el servicio la última vez que se le preguntó?
 *
 * Sin esto, "el servicio no responde" y "el servicio dice que no hay proveedores
 * configurados" acaban en el mismo mensaje en pantalla, y son dos problemas con
 * dos arreglos completamente distintos: uno es CORS o una caída, el otro son
 * unas credenciales que faltan. Colapsarlos manda a mirar donde no es.
 */
let contesto = true;
let pase = "";
let paseHasta = 0;

export const current = (): AccountView | null => cuenta;
export const providers = (): readonly string[] => proveedores;
/** `false` = no se pudo hablar con el servicio de cuentas (caída, CORS, red). */
export const reachable = (): boolean => contesto;
/** ¿Hay sesión? Solo es fiable después de `load()`. */
export const signedIn = (): boolean => cuenta !== null;

/** Pregunta quién eres y qué proveedores hay. Se llama una vez al arrancar. */
export async function load(): Promise<AccountView | null> {
  if (!on()) return null;
  const [me, provs] = await Promise.all([
    call<{ account: AccountView | null }>("/account/me"),
    call<{ providers: string[] }>("/auth/providers"),
  ]);
  // `null` aquí no es "no hay proveedores": es "no hubo respuesta". La ruta
  // existe siempre y siempre devuelve una lista, aunque esté vacía.
  contesto = provs !== null;
  cuenta = me?.account ?? null;
  proveedores = provs?.providers ?? [];
  if (cuenta) void refreshTicket();
  return cuenta;
}

/* ---------------- entrar y salir ---------------- */

/**
 * Manda el navegador al proveedor. No vuelve: esto termina en una redirección.
 *
 * Lo que traes de invitado (`gid`, nombre) viaja en la petición para que el
 * servicio pueda fundirlo con la cuenta. Nunca "empiezas de cero".
 */
export function startLogin(
  provider: string,
  opts: { guestId?: string; displayName?: string; link?: boolean; next?: string } = {},
): string {
  const q = new URLSearchParams({ next: opts.next ?? location.href.split("?")[0] });
  if (opts.link) q.set("link", "1");
  if (opts.guestId) q.set("gid", opts.guestId);
  if (opts.displayName) q.set("name", opts.displayName);
  return url(`/auth/${provider}/start?${q}`);
}

/** Segundo paso del alta. El billete lo pone la cookie; aquí solo va el nick. */
export async function completeSignUp(
  nickname: string,
  guest: { guestId?: string; displayName?: string } = {},
): Promise<{ ok: true; account: AccountView } | { ok: false; message: string }> {
  const r = await call<{ account?: AccountView; message?: string; error?: string }>(
    "/auth/complete", { method: "POST", body: JSON.stringify({ nickname, ...guest }) },
  );
  if (!r) return { ok: false, message: "No se pudo contactar con el servicio de cuentas." };
  if (!r.account) return { ok: false, message: r.message ?? mensajeDe(r.error ?? "") };
  cuenta = r.account;
  void refreshTicket();
  return { ok: true, account: r.account };
}

/** ¿Está libre ese nick? Para poder decirlo mientras se teclea. */
export async function nicknameFree(n: string): Promise<{ free: boolean; message: string }> {
  const r = await call<{ free: boolean; message?: string; reason?: string }>(
    `/auth/nickname-free?n=${encodeURIComponent(n)}`,
  );
  if (!r) return { free: false, message: "" };
  return { free: r.free, message: r.free ? "" : (r.message ?? mensajeDe(r.reason ?? "")) };
}

export async function logout(): Promise<void> {
  await call("/auth/logout", { method: "POST" });
  cuenta = null;
  pase = "";
  paseHasta = 0;
}

export async function setDisplayName(name: string): Promise<AccountView | null> {
  const r = await call<{ account: AccountView }>("/account/display-name", {
    method: "POST", body: JSON.stringify({ name }),
  });
  if (r?.account) cuenta = r.account;
  return cuenta;
}

/**
 * Ponerse (o quitarse, con `null`) un cosmético.
 *
 * Devuelve la cuenta entera porque eso es lo que contesta el servicio: lo que se
 * enseña después es lo que el servidor acaba de decidir, no lo que aquí se creía
 * que iba a pasar. Si el objeto está bloqueado o no existe, `cuenta` no se toca
 * y la interfaz sigue enseñando lo que de verdad hay puesto.
 */
export async function equip(
  slot: string,
  id: string | null,
): Promise<{ ok: boolean; message: string }> {
  const r = await call<{ account?: AccountView; error?: string }>("/account/loadout", {
    method: "POST", body: JSON.stringify({ slot, id }),
  });
  if (r?.account) { cuenta = r.account; return { ok: true, message: "" }; }
  if (!r) return { ok: false, message: "No se pudo contactar con el servicio de cuentas." };
  return {
    ok: false,
    message: r.error === "locked"
      ? "Todavía no es tuyo."
      : "Ese objeto no existe en esta versión del juego.",
  };
}

/** Lo que llevas puesto en un hueco, o `undefined` si no hay sesión o va vacío. */
export function equipped(slot: string): string | undefined {
  return cuenta?.loadout?.[slot];
}

/** Los skus que constan comprados. Vacío sin sesión. */
export function owned(): readonly string[] {
  return cuenta?.entitlements ?? [];
}

export async function unlink(provider: string): Promise<{ ok: boolean; message: string }> {
  const r = await call<{ account?: AccountView; error?: string }>(
    `/account/unlink/${provider}`, { method: "POST" },
  );
  if (r?.account) { cuenta = r.account; return { ok: true, message: "" }; }
  return {
    ok: false,
    message: r?.error === "last-one"
      ? "Es tu única forma de entrar: vincula otra antes de quitar esta."
      : "No se pudo desvincular.",
  };
}

export async function deleteAccount(): Promise<boolean> {
  const r = await call<{ deleted: boolean }>("/account", { method: "DELETE" });
  if (r?.deleted) { cuenta = null; pase = ""; paseHasta = 0; }
  return Boolean(r?.deleted);
}

/** Funde un id de invitado con la cuenta actual. */
export async function mergeGuest(guestId: string): Promise<boolean> {
  const r = await call<{ merged: boolean }>("/account/guest", {
    method: "POST", body: JSON.stringify({ guestId }),
  });
  return Boolean(r?.merged);
}

/* ---------------- el pase para el servidor de partida ---------------- */

/** Margen antes de dar el pase por gastado. El pase dura 120 s. */
const MARGEN_MS = 20_000;

/**
 * Pase vigente, o cadena vacía. **Síncrono a propósito**: la URL del WebSocket
 * se construye así en cada intento de reconexión, y no se puede esperar ahí.
 */
export function ticketNow(): string {
  if (!pase || Date.now() > paseHasta - MARGEN_MS) {
    // Se pide uno nuevo para la PRÓXIMA vez y se devuelve lo que hay. Conectar
    // sin pase no deja a nadie fuera: se entra de invitado, que es peor que
    // entrar con cuenta y muchísimo mejor que no entrar.
    if (cuenta) void refreshTicket();
    return "";
  }
  return pase;
}

/** Pide un pase nuevo. Se llama al entrar al lobby y cuando el actual caduca. */
export async function refreshTicket(): Promise<string> {
  if (!cuenta) return "";
  const r = await call<{ ticket: string }>("/account/ticket");
  if (!r?.ticket) return "";
  pase = r.ticket;
  // 120 s es lo que dura del lado del servidor (EDGE_TTL). Aquí se guarda con
  // el mismo número, no con uno adivinado: si allí cambia, esto se queda corto,
  // que es el lado seguro del error.
  paseHasta = Date.now() + 120_000;
  return pase;
}

/* ---------------- la vuelta del proveedor ---------------- */

/**
 * Lee el resultado de la URL y **la limpia**. Dejar `?auth_ok=in` puesto hace
 * que recargar la página vuelva a enseñar el aviso de "sesión iniciada" para
 * siempre, y que ese parámetro acabe en cualquier enlace que se comparta.
 */
export function readAuthOutcome(search = location.search, clean = true): AuthOutcome {
  const q = new URLSearchParams(search);
  const limpiar = (): void => {
    if (!clean || typeof history === "undefined") return;
    for (const k of ["auth_ok", "auth_error", "auth_nuevo", "sugerencia"]) q.delete(k);
    const resto = q.toString();
    history.replaceState(null, "", location.pathname + (resto ? `?${resto}` : "") + location.hash);
  };

  if (q.get("auth_nuevo") === "1") {
    const s = q.get("sugerencia") ?? "";
    limpiar();
    return { kind: "nuevo", suggestion: s };
  }
  const ok = q.get("auth_ok");
  if (ok === "in" || ok === "linked") {
    limpiar();
    return { kind: ok === "in" ? "in" : "linked" };
  }
  const err = q.get("auth_error");
  if (err) {
    limpiar();
    return { kind: "error", reason: err };
  }
  return { kind: "none" };
}

/** Texto para la persona. Un código de error crudo en pantalla no es un mensaje. */
export function mensajeDe(reason: string): string {
  switch (reason) {
    case "cancelled": return "No pasa nada: no se hizo nada.";
    case "bad-state": return "El intento caducó. Vuelve a probar.";
    case "no-session": return "Se cerró la sesión por el camino. Entra otra vez.";
    case "provider-off": return "Esa forma de entrar no está disponible ahora mismo.";
    case "wrong-audience": return "La respuesta del proveedor no era para este juego.";
    case "exchange-failed": return "El proveedor no confirmó la entrada. Prueba otra vez.";
    case "taken": return "Ese nick ya está cogido.";
    case "bad-ticket": return "El alta caducó. Vuelve a empezar.";
    case "no-ticket": return "Tu navegador no envió la cookie del alta. Suele ser el "
      + "bloqueo de cookies de terceros: pruébalo permitiéndolas para este sitio, "
      + "o en otro navegador.";
    case "": return "";
    default: return "No se pudo completar la entrada.";
  }
}

/** Nombre bonito de un proveedor, para los botones. */
export function nombreDe(provider: string): string {
  return provider === "google" ? "Google" : provider === "discord" ? "Discord"
    : provider === "steam" ? "Steam" : provider;
}

/** Solo para pruebas: deja el módulo como recién cargado. */
export function resetAccountState(): void {
  cuenta = null;
  proveedores = [];
  contesto = true;
  pase = "";
  paseHasta = 0;
}
